/**
 * @fileoverview Input/output schemas for story-modifier-v1.
 *
 * This agent takes a natural language change request from the author and
 * applies modifications across three planning objects: storylines, character
 * arcs, and the writtenPlot. The output includes all three modified objects
 * plus commentary explaining the changes.
 *
 * This is a Tier 2 agent: the result is shown to the user for confirmation
 * before being persisted.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { StructurePhaseSchema } from '@/domains/story-planning/schemas/structure-phases';
import { CharacterAttributesSchema } from '@/domains/characters/schemas/character-sub-entities-v2';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.writtenPlot.schema)
  .merge(AGENT_SCHEMA_OBJECTS.existingStorylines.schema)
  .merge(AGENT_SCHEMA_OBJECTS.characterArcs.schema)
  .merge(AGENT_SCHEMA_OBJECTS.characters.schema)
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.projectedWordCount.schema)
  .extend({
    changeRequest: z.string().describe(
      'The author\'s natural language description of the changes they want applied to the story. ' +
      'This can reference specific phases, characters, storylines, or narrative elements.'
    ),
    scopeGuidance: z.string().optional().describe(
      'Rendered scope guidance string that specifies the target paragraph density for the writtenPlot ' +
      'based on the story\'s projected word count.'
    ),
  });

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

const ModifiedCharacterArcSchema = z.object({
  characterId: z.string().describe('The ID of the character whose arc was modified. Must match an existing character.'),
  characterName: z.string().describe('The name of the character (for readability).'),
  arcName: z.string().describe('The name of the character arc.'),
  arcSummary: z.string().describe('Updated 1-3 sentence summary of the character\'s transformation.'),
  beats: z.array(z.object({
    id: z.string().describe('Beat ID. Preserve existing IDs for unchanged beats; use new UUIDs for new beats.'),
    globalPhase: StructurePhaseSchema.describe('Where in the overall story timeline this beat occurs.'),
    arcPhase: StructurePhaseSchema.describe('What structural function this beat serves for this character\'s arc.'),
    internalState: z.string().describe('The psychological shift or realization at this moment.'),
    behavior: z.string().optional().describe('Observable action triggered by the internal state.'),
    arcProgress: z.number().describe('Position on truth/lie spectrum (-1 to +1).'),
  })).describe('The updated beats for this character arc.'),
});

const ModifiedStorylineSchema = z.object({
  id: z.string().describe('Storyline ID. Preserve existing IDs for modified storylines; use new UUIDs for new storylines.'),
  name: z.string().describe('Short, evocative name for the storyline (2-4 words).'),
  summary: z.string().optional().describe('Brief description of the storyline (1-3 sentences).'),
  priority: z.enum(['A', 'B', 'C']).describe('Storyline priority (A = External Spine, B = Internal/Thematic, C = Subplot).'),
  colorIndex: z.number().describe('Color palette index (integer 0-15) for visual display.'),
  beats: z.array(z.object({
    id: z.string().describe('Beat ID. Preserve existing IDs for unchanged beats; use new UUIDs for new beats.'),
    globalPhase: StructurePhaseSchema.describe('Where in the global story timeline this beat occurs.'),
    storylinePhase: StructurePhaseSchema.describe('What structural function this beat serves for this storyline.'),
    plotEvent: z.string().describe('The concrete, observable event that occurs.'),
    narrativeState: z.string().optional().describe('Emotional or thematic state at this moment.'),
    goalProgress: z.number().describe('Progress toward the storyline goal (-1 to +1).'),
    order: z.number().default(0).describe('Sequence order (integer >= 0) within the globalPhase cell.'),
  })).describe('The sequence of plot events defining this storyline.'),
  storyQuestions: z.array(z.object({
    id: z.string().describe('Question ID.'),
    question: z.string().describe('The dramatic question.'),
    status: z.enum(['open', 'answered']).describe('Whether the question is resolved.'),
  })).default([]).describe('Dramatic questions belonging to this storyline.'),
});

const CharacterChangeSchema = z.object({
  characterId: z.string().describe('The character ID. Use the existing ID for modified/removed characters; generate a new UUID for added characters.'),
  action: z.enum(['added', 'removed', 'modified']).describe('The type of change: "added" for new characters, "removed" for deleted characters, "modified" for property changes.'),
  characterName: z.string().describe('The character\'s name.'),
  category: z.enum(['major', 'minor', 'extra']).describe('The character\'s importance level.'),
  modifiedProperties: CharacterAttributesSchema.partial().optional().describe(
    'An object containing only the character properties that were changed. ' +
    'Omit unchanged properties. Required for "added" and "modified" actions; omit for "removed".'
  ),
});

export const OutputSchema = z.object({
  summary: z.string().describe(
    'A concise overview of the plot (100-150 words) that weaves together the story summary, its central theme, and the main conflict. ' +
    'This summary is displayed as a subtitle on the Active Story Outline card and used as context by other AI agents. ' +
    'Must reflect the modified plot content.'
  ),
  commentary: z.string().optional().describe(
    'A summary explaining what changes were made across all planning objects and why. ' +
    'Be specific about which storylines, characters, character arcs, and plot sections were modified. ' +
    'Note any storylines or characters that were added or removed. ' +
    'Explain how the changes maintain narrative coherence across the objects.'
  ),
  characters: z.array(CharacterChangeSchema).default([]).describe(
    'An array of character change operations. Only include characters that were added, removed, or had their properties modified. ' +
    'Return an empty array if no character changes are needed.'
  ),
  storylines: z.array(ModifiedStorylineSchema).describe(
    'The complete, updated array of storylines. May add new storylines or remove existing ones. ' +
    'Preserve IDs and data for storylines that were not changed. ' +
    'Every storyline from the input must appear in the output unless explicitly removed by the change request.'
  ),
  characterArcs: z.array(ModifiedCharacterArcSchema).describe(
    'The updated character arcs for all characters remaining in the story (including newly added ones). ' +
    'Omit arcs for removed characters. Include arcs for added characters if they are major or minor.'
  ),
  writtenPlot: z.string().describe(
    'The updated plot outline in Markdown format. ' +
    'Use ## headings for each structural phase (Initial State, Inciting Incident, Break Into Two, Midpoint, Break Into Three, Climax, Final State). ' +
    'Use **bold** for character names and key events, *italic* for emotional states. ' +
    'Separate paragraphs with double newlines. Do NOT use HTML tags. ' +
    'Write in present tense, third person. ' +
    'The narrative should reflect the changes made to storylines and character arcs.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
